import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  changeCourseStatusAction,
  checkSavedCourseAction,
  clearDetailsCourseAction,
  deleteSavedCourseAction,
  getDetailsCourseAction,
  saveCourseAction,
} from "@redux/modules/course/slices";
import { AboutTab } from "../AboutTab";
import { CoursesContentTab } from "../CoursesContentTab";
import { ReviewsTab } from "../ReviewsTab";
import {
  enrollAction,
  checkEnrollmentAction,
  clearCheckEnrollmentAction,
  clearEnrollStatusAction,
} from "@redux/modules/enrollment/slices";
import {
  ADMIN_ROLE,
  APP_TITLE_SUFFIX,
  COURSE_STATUS,
  INSTRUCTOR_ROLE,
  STUDENT_ROLE,
} from "@constants";
import {
  isSubscribedAction,
  subscribeAction,
  unsubscribeAction,
} from "@redux/actions";
import { useAccessControl } from "@hooks";

const useCourseInfo = (courseId) => {
  const [defaultTab, setDefaultTab] = useState(-1);
  const { getDetailsCourse, saveCourse, checkSavedCourse, deleteSavedCourse } =
    useSelector((state) => state.course);
  const { isSaved, processing: processingCheck } = checkSavedCourse;
  const { data: courseDetails, processing } = getDetailsCourse;
  const { processing: saveCourseProcessing } = saveCourse;
  const { processing: unsaveCourseProcessing } = deleteSavedCourse;
  const savingProcessing = saveCourseProcessing || unsaveCourseProcessing;

  const { checkEnrollment, enroll } = useSelector((state) => state.enrollment);
  const { data: enrollInfo, processing: checkEnrollProcessing } =
    checkEnrollment;
  const { processing: enrollProcessing } = enroll;

  const { profile } = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuth, role } = useAccessControl([STUDENT_ROLE, INSTRUCTOR_ROLE]);

  const tabRef = useRef();

  const isOwner =
    courseDetails && profile
      ? courseDetails.user?.id === profile.data?.id
      : false;
  const isAdmin = role === ADMIN_ROLE;

  useEffect(() => {
    if (processing) return;

    if (location.hash === "#reviews" || location.hash === "#edit-review") {
      setDefaultTab(2);
      tabRef?.current?.scrollIntoView();
    }
  }, [processing]);

  useEffect(() => {
    dispatch(getDetailsCourseAction(courseId));
    dispatch(checkEnrollmentAction(courseId));
    dispatch(checkSavedCourseAction(courseId));
    return () => {
      dispatch(clearCheckEnrollmentAction());
      dispatch(clearDetailsCourseAction());
      dispatch(clearEnrollStatusAction());
    };
  }, [courseId]);

  useLayoutEffect(() => {
    if (isOwner || isAdmin) {
      return;
    }
    if (
      courseDetails?.status !== COURSE_STATUS.APPROVE ||
      !courseDetails?.isActive
    ) {
      navigate("/404", { replace: true });
    }
    document.title = `${courseDetails?.title || ""} | ${APP_TITLE_SUFFIX}`;
    return () => {
      document.title = APP_TITLE_SUFFIX;
    };
  }, [courseDetails, isOwner]);

  const handleReport = () => {};
  const handleEnroll = () => {
    dispatch(enrollAction({ courseId, callback: handleRedirectLearning }));
  };

  const handleRedirectLearning = () => {
    navigate(`/learning/${courseId}`, { replace: true });
  };

  const handleSaveCourse = () => {
    if (isSaved) {
      dispatch(deleteSavedCourseAction({ courseId }));
    } else {
      dispatch(saveCourseAction({ courseId }));
    }
  };

  const instructorInfo = courseDetails?.user || null;

  useEffect(() => {
    if (instructorInfo) {
      dispatch(isSubscribedAction(instructorInfo.id));
    }
  }, [instructorInfo]);

  const subscribe = useSelector((state) => state.subscription.subscribe);
  const unsubscribe = useSelector((state) => state.subscription.unsubscribe);
  const isSubscribed = useSelector((state) => state.subscription.isSubscribed);

  const handleSubscribe = (id) => {
    if (!isAuth) {
      navigate("/sign-in");
      return;
    }
    dispatch(subscribeAction(id));
  };

  const handleUnsubscribe = (id) => {
    if (!isAuth) {
      navigate("/sign-in");
      return;
    }
    dispatch(unsubscribeAction(id));
  };

  const isEnrolled = enrollInfo && checkEnrollProcessing === false;

  const handleLearn = () => {
    navigate(`/learning/${courseId}`);
  };

  const handleChangeStatus = (newStatus) => {
    dispatch(
      changeCourseStatusAction({
        data: [courseId, newStatus],
      })
    );
  };

  const tabInfoList = [
    {
      id: "nav-about",
      title: "About",
      content: (
        <AboutTab
          requirements={courseDetails?.requirement}
          description={courseDetails?.description}
          benefits={courseDetails?.learn}
        />
      ),
    },
    {
      id: "nav-courses",
      title: "Courses Content",
      content: <CoursesContentTab sectionList={courseDetails?.sections} />,
    },
    {
      id: "nav-reviews",
      title: "Reviews",
      content: (
        <ReviewsTab
          courseId={courseId}
          author={courseDetails?.user}
          loadReviews={courseDetails ? true : false}
        />
      ),
    },
  ];

  return {
    processing,
    courseDetails,
    handleSaveCourse,
    handleReport,
    handleEnroll,
    isEnrolled,
    checkEnrollProcessing,
    enrollProcessing,
    instructorInfo,
    savingProcessing,
    handleLearn,
    tabInfoList,
    handleSubscribe,
    handleUnsubscribe,
    handleChangeStatus,
    subscribe,
    unsubscribe,
    isSubscribed,
    defaultTab,
    tabRef,
    isSaved,
    processingCheck,
    isAuth,
    isOwner,
    isAdmin,
  };
};

/* required: display name */
useCourseInfo.displayName = "useCourseInfo";

/* required: props types */
useCourseInfo.propTypes = {
  courseId: PropTypes.string,
};

/* required: default props */
useCourseInfo.defaultProps = {};

export { useCourseInfo };
